import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import type { GetOffersQuery, GetOffersResponse } from "@codway-dev/shared";
import { OfferService } from "../services/offer.service";
import { isDeepEqual } from "../utils/deep-equal";

// ─── Types ────────────────────────────────────────────────────────────────────

export type OfferSummary = GetOffersResponse["data"][number];

interface OffersListState {
  offers: OfferSummary[];
  activeCount: number;
  activeLimit: number;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  /** Last query used by loadOffers — re-applied by refetch. */
  query: Partial<GetOffersQuery>;
  /**
   * Offers whose deletion has been staged by the merchant but not yet
   * committed to the backend. Removed from the visible list but still
   * present in `offers` so Descartar can restore them.
   */
  pendingDeleteIds: string[];
  /** True while commitPending() is in flight. */
  isCommitting: boolean;
}

interface OffersListActions {
  loadOffers: (
    query?: Partial<GetOffersQuery>,
    force?: boolean
  ) => Promise<void>;
  refetch: () => Promise<void>;
  toggleActive: (id: string, nextIsActive: boolean) => Promise<void>;
  duplicateOffer: (id: string) => Promise<void>;

  // Staged-delete flow
  stageDelete: (id: string) => void;
  commitPending: () => Promise<void>;
  discardPending: () => void;
}

type OffersListStore = OffersListState & OffersListActions;

// ─── Constants ───────────────────────────────────────────────────────────────

const STALE_MS = 30_000;

const INITIAL_STATE: OffersListState = {
  offers: [],
  activeCount: 0,
  activeLimit: 0,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  query: {},
  pendingDeleteIds: [],
  isCommitting: false,
};

// ─── Selectors ───────────────────────────────────────────────────────────────

/**
 * The merchant's effective active count — what they see in the banner and
 * what the limit block reads from. Server count minus any active-modal
 * offers they've staged for deletion but not yet saved.
 */
export const selectEffectiveActiveCount = (state: OffersListStore) => {
  let delta = 0;
  for (const id of state.pendingDeleteIds) {
    const offer = state.offers.find((o) => o.id === id);
    if (offer && offer.is_active && offer.display_type === "modal") {
      delta += 1;
    }
  }
  return Math.max(0, state.activeCount - delta);
};

export const selectIsLimitReached = (state: OffersListStore) =>
  state.activeLimit > 0 &&
  selectEffectiveActiveCount(state) >= state.activeLimit;

export const selectHasPendingChanges = (state: OffersListStore) =>
  state.pendingDeleteIds.length > 0;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useOffersListStore = create<OffersListStore>()(
  devtools(
    immer((set, get) => ({
      ...INITIAL_STATE,

      loadOffers: async (query = {}, force = false) => {
        const { isLoading, lastFetchedAt, query: previousQuery } = get();
        if (isLoading) return;
        // Skip the network only if (a) we're not forcing, (b) the cache
        // is fresh, AND (c) the requested query matches the last one we
        // ran. Without (c), navigating between list pages within
        // STALE_MS leaves the previous page's data in the store and the
        // new page renders empty until the timeout expires.
        const sameQuery = isDeepEqual(query, previousQuery);
        if (
          !force &&
          sameQuery &&
          lastFetchedAt !== null &&
          Date.now() - lastFetchedAt < STALE_MS
        ) {
          return;
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
          // When the query shape changes (e.g. navigating between list
          // pages that filter by different kinds), drop the previous
          // list's data immediately. Without this, the next page would
          // render the previous page's rows on the first paint after
          // useEffect fires, then flicker to the correct list when the
          // fetch lands. Resetting lastFetchedAt also re-arms the
          // first-paint loader on the destination page.
          if (!sameQuery) {
            state.offers = [];
            state.lastFetchedAt = null;
          }
          state.query = query;
        });

        try {
          const response = await OfferService.list({
            page: 1,
            limit: 100,
            ...query,
          });
          set((state) => {
            state.offers = response.data;
            state.activeCount = response.meta.modal_active_count;
            state.activeLimit = response.meta.modal_active_limit;
            state.isLoading = false;
            state.lastFetchedAt = Date.now();
            // Drop any staged deletions for offers that no longer exist on
            // the server (e.g. someone else deleted them in another tab).
            if (state.pendingDeleteIds.length > 0) {
              const existingIds = new Set(response.data.map((o) => o.id));
              state.pendingDeleteIds = state.pendingDeleteIds.filter((id) =>
                existingIds.has(id)
              );
            }
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error
                ? err.message
                : "No se pudieron cargar las ofertas";
            state.isLoading = false;
          });
        }
      },

      refetch: async () => {
        await get().loadOffers(get().query, true);
      },

      toggleActive: async (id, nextIsActive) => {
        const current = get().offers.find((o) => o.id === id);
        if (!current) return;
        const isModal = current.display_type === "modal";
        const countDelta = isModal ? (nextIsActive ? 1 : -1) : 0;

        // Optimistic update
        set((state) => {
          const row = state.offers.find((o) => o.id === id);
          if (row) row.is_active = nextIsActive;
          state.activeCount += countDelta;
        });

        try {
          await OfferService.update(id, { is_active: nextIsActive });
        } catch (err) {
          // Rollback
          set((state) => {
            const row = state.offers.find((o) => o.id === id);
            if (row) row.is_active = !nextIsActive;
            state.activeCount -= countDelta;
          });
          throw err instanceof Error
            ? err
            : new Error("No se pudo cambiar el estado del upsell");
        }
      },

      duplicateOffer: async (id) => {
        // Fetch full detail so we can clone the nested config/target/triggers.
        const detail = await OfferService.get(id);
        try {
          await OfferService.create({
            internal_name: `${detail.internal_name} (copia)`,
            // Always create duplicates as inactive so they don't compete for
            // the active-modal limit slot until the merchant reviews them.
            is_active: false,
            priority: detail.priority,
            config: detail.config,
            target: detail.target,
            triggers: detail.triggers,
          });
          await get().refetch();
        } catch (err) {
          throw err instanceof Error
            ? err
            : new Error("No se pudo duplicar el upsell");
        }
      },

      stageDelete: (id) =>
        set((state) => {
          if (state.pendingDeleteIds.includes(id)) return;
          // Guard: the id must exist in the current list.
          if (!state.offers.some((o) => o.id === id)) return;
          state.pendingDeleteIds.push(id);
        }),

      commitPending: async () => {
        const pending = get().pendingDeleteIds;
        if (pending.length === 0) return;

        set((state) => {
          state.isCommitting = true;
        });

        const results = await Promise.allSettled(
          pending.map((id) => OfferService.remove(id))
        );

        const failedIds = pending.filter(
          (_, i) => results[i].status === "rejected"
        );

        set((state) => {
          // Only keep the failed ones around so the merchant can retry.
          state.pendingDeleteIds = failedIds;
          state.isCommitting = false;
        });

        // Refresh list to reflect actual BE state (succeeded deletes gone,
        // counts recomputed from server).
        await get().refetch();

        if (failedIds.length > 0) {
          const message =
            failedIds.length === pending.length
              ? "No se pudo eliminar ningún upsell. Inténtalo de nuevo."
              : `No se pudieron eliminar ${failedIds.length} upsell(s). Revisa e inténtalo de nuevo.`;
          throw new Error(message);
        }
      },

      discardPending: () =>
        set((state) => {
          state.pendingDeleteIds = [];
        }),
    })),
    { name: "OffersListStore" }
  )
);
